import {
  fetchPowersPricesCatalog,
  mergePowerWithApiBasics,
  type PowerInfo,
} from '@/lib/powers-data';

const CACHE_KEY = 'xat_ajuda_catalog_prices_v1';
const CACHE_TTL_MS = 5 * 60 * 1000;

type CacheBlob = {
  at: number;
  bySlug: Record<string, PowerInfo>;
};

function readCache(): CacheBlob | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheBlob;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.at !== 'number') return null;
    if (!parsed.bySlug || typeof parsed.bySlug !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(blob: CacheBlob) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(blob));
  } catch {
    /* quota / private mode */
  }
}

function pickBatch(slugs: string[], bySlug: Record<string, PowerInfo>): Record<string, PowerInfo> {
  const batch: Record<string, PowerInfo> = {};
  for (const slug of slugs) {
    const power = bySlug[slug];
    if (power) batch[slug] = power;
  }
  return batch;
}

export function applyCatalogApiOverrides(
  powers: PowerInfo[],
  apiBySlug: Record<string, PowerInfo>
): PowerInfo[] {
  if (Object.keys(apiBySlug).length === 0) return powers;

  return powers.map((power) => {
    const api = apiBySlug[power.slug];
    return api ? mergePowerWithApiBasics(power, api) : power;
  });
}

/**
 * Sincroniza dados básicos do catálogo via api.xatblog.net/prices (1 request).
 * Usa cache de sessão (5 min). Mantém powerinfo só para detalhe/menções.
 */
export async function syncCatalogPowersFromApi(
  slugs: string[],
  onBatch: (batch: Record<string, PowerInfo>) => void,
  signal?: AbortSignal
): Promise<void> {
  const uniqueSlugs = [...new Set(slugs.map((s) => s.trim().toLowerCase()).filter(Boolean))];
  if (uniqueSlugs.length === 0) return;

  const cached = readCache();
  const now = Date.now();
  if (cached && now - cached.at <= CACHE_TTL_MS) {
    const batch = pickBatch(uniqueSlugs, cached.bySlug);
    const missing = uniqueSlugs.filter((slug) => !cached.bySlug[slug]);
    if (Object.keys(batch).length > 0) onBatch(batch);
    if (missing.length === 0) return;
  }

  if (signal?.aborted) return;

  const catalog = await fetchPowersPricesCatalog(signal);
  if (!catalog || signal?.aborted) return;

  const bySlug: Record<string, PowerInfo> = {};
  for (const power of catalog) {
    bySlug[power.slug] = power;
  }

  writeCache({ at: Date.now(), bySlug });

  const batch = pickBatch(uniqueSlugs, bySlug);
  if (Object.keys(batch).length > 0) onBatch(batch);
}
